// src/pages/SupportPage.js
//
// Support page — 3 contact options + FAQ grid + contact CTA. Uses
// <LegalPageShell prose={false}> because this page has its own
// card-based layout rather than legal prose, but reuses the shell's
// nav/footer/title-block.

import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Mail, HelpCircle, FileText, ExternalLink } from 'lucide-react';
import LegalPageShell from '../components/LegalPageShell';

const SUPPORT_OPTIONS = [
  {
    icon: Mail,
    title: 'Email Support',
    description: 'Get help via email. We typically respond within 24 hours.',
    action: 'support@nexusmanager.app',
    actionType: 'email',
  },
  {
    icon: HelpCircle,
    title: 'FAQ',
    description: 'Find answers to commonly asked questions about Nexus.',
    action: 'View FAQ',
    actionType: 'faq',
  },
  {
    icon: FileText,
    title: 'Documentation',
    description: 'Learn how to use all of Nexus features with our guides.',
    action: 'View Docs',
    actionType: 'docs',
  },
];

const FAQS = [
  {
    question: 'Is Nexus really free?',
    answer:
      'Yes — Nexus offers a free plan with up to 150 inventory items, 50 sales, and 50 expenses. Upgrade to a paid plan anytime for higher limits and additional features.',
  },
  {
    question: 'Can I cancel my subscription anytime?',
    answer:
      "Yes, cancel anytime from your Account → Billing settings. You'll keep access until the end of your billing period.",
  },
  {
    question: 'How do I add inventory items?',
    answer:
      'Go to the Inventory page and click "Add Item." Enter product details, upload photos, set pricing, or use the barcode scanner to auto-fill.',
  },
  {
    question: 'Can I export my data?',
    answer:
      'Yes. Settings → Data → "Download all data" produces a single ZIP with every collection as CSV (inventory, sales, expenses, packages, scraped orders).',
  },
  {
    question: 'Is my data secure?',
    answer:
      'All data is encrypted in transit (HTTPS/TLS) and at rest. We use industry-standard authentication and never share or sell your data.',
  },
  {
    question: 'How do I track packages?',
    answer:
      'Go to Orders & Tracking, add tracking numbers, and we handle the rest. Status updates fire automatically via webhook from major carriers (USPS, UPS, FedEx, DHL).',
  },
];

const SupportPage = () => {
  useEffect(() => {
    document.title = 'Support | Nexus';
  }, []);

  return (
    <LegalPageShell
      title="How can we help?"
      subtitle="Choose a support option or browse our FAQ below."
      maxWidth={1024}
      prose={false}
    >
      {/* Support options grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-12">
        {SUPPORT_OPTIONS.map((option) => {
          const Icon = option.icon;
          return (
            <div
              key={option.title}
              className="rounded-md border p-5 transition-colors"
              style={{
                backgroundColor: 'var(--color-card)',
                borderColor: 'var(--color-border)',
              }}
            >
              <div
                className="w-10 h-10 rounded-md flex items-center justify-center mb-3"
                style={{
                  backgroundColor: 'color-mix(in srgb, rgb(var(--accent-primary)) 12%, transparent)',
                  color: 'rgb(var(--accent-primary))',
                }}
              >
                <Icon className="w-5 h-5" />
              </div>
              <h3
                className="text-base font-semibold mb-1.5"
                style={{ color: 'var(--color-text)' }}
              >
                {option.title}
              </h3>
              <p className="text-sm mb-3 leading-relaxed" style={{ color: 'var(--color-text-muted)' }}>
                {option.description}
              </p>
              {option.actionType === 'email' ? (
                <a
                  href={`mailto:${option.action}`}
                  className="text-sm inline-flex items-center gap-1 transition-opacity hover:opacity-80"
                  style={{ color: 'rgb(var(--accent-primary))' }}
                >
                  {option.action}
                  <ExternalLink className="w-3 h-3" />
                </a>
              ) : option.actionType === 'docs' ? (
                <Link
                  to="/docs"
                  className="text-sm transition-opacity hover:opacity-80"
                  style={{ color: 'rgb(var(--accent-primary))' }}
                >
                  {option.action}
                </Link>
              ) : (
                <a
                  href="#faq"
                  className="text-sm transition-opacity hover:opacity-80"
                  style={{ color: 'rgb(var(--accent-primary))' }}
                >
                  {option.action}
                </a>
              )}
            </div>
          );
        })}
      </div>

      {/* FAQ grid */}
      <div id="faq" className="mb-12">
        <h2
          className="text-xl sm:text-2xl font-bold mb-6 tracking-tight"
          style={{ color: 'var(--color-text)' }}
        >
          Frequently asked questions
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {FAQS.map((faq) => (
            <div
              key={faq.question}
              className="rounded-md border p-5"
              style={{
                backgroundColor: 'var(--color-card)',
                borderColor: 'var(--color-border)',
              }}
            >
              <h3
                className="text-sm font-semibold mb-2"
                style={{ color: 'var(--color-text)' }}
              >
                {faq.question}
              </h3>
              <p className="text-sm leading-relaxed" style={{ color: 'var(--color-text-muted)' }}>
                {faq.answer}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Contact CTA — accent-tinted panel matching the HomePage final CTA treatment */}
      <div
        className="relative overflow-hidden rounded-md border p-6 sm:p-8 text-center"
        style={{
          background: `linear-gradient(135deg, color-mix(in srgb, rgb(var(--accent-primary)) 12%, var(--color-card)) 0%, var(--color-card) 70%)`,
          borderColor: 'color-mix(in srgb, rgb(var(--accent-primary)) 30%, var(--color-border))',
        }}
      >
        <div
          className="absolute -top-20 -right-20 w-[300px] h-[300px] rounded-full pointer-events-none"
          style={{
            background: 'radial-gradient(circle, rgb(var(--accent-primary) / 0.25) 0%, transparent 70%)',
            filter: 'blur(60px)',
          }}
        />
        <div className="relative z-10">
          <h3
            className="text-lg sm:text-xl font-semibold mb-2"
            style={{ color: 'var(--color-text)' }}
          >
            Still need help?
          </h3>
          <p className="mb-5 text-sm sm:text-base" style={{ color: 'var(--color-text-muted)' }}>
            Our team usually responds within 24 hours. Email us directly:
          </p>
          <a
            href="mailto:support@nexusmanager.app"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md font-semibold text-white transition-opacity hover:opacity-90"
            style={{ backgroundColor: 'rgb(var(--accent-primary))' }}
          >
            <Mail className="w-4 h-4" />
            Contact Support
          </a>
        </div>
      </div>
    </LegalPageShell>
  );
};

export default SupportPage;
